import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { BirthdateValues } from '@ca/common'
import { getInvoiceFormSchema } from '../stepSchemas'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import { useCountries } from 'hooks/options/useCountries'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { getSaveDate } from '@user/customer/modals/Account/Invoice/useInvoiceForm'
import useCreateCustomerStore from '../useCreateCustomerStore'
import {
  getInvoiceFormDefaults,
  hasAnyInvoiceFormValues,
  isKidsAccount,
} from '../utils'

interface Props {
  submitRef: React.MutableRefObject<() => void>
  onSuccess: () => void
  setIsPending?: (v: boolean) => void
}

export default function InvoiceStep({ submitRef, onSuccess, setIsPending }: Props) {
  const { t } = useTranslation(['user'])
  const { data, updateData } = useCreateCustomerStore()
  const config = useRemoteConfig()
  const countries = useCountries()
  const hasVat = config?.region?.vat !== 'hidden'
  const kids = isKidsAccount(data)
  const showVat = hasVat && !kids

  const schema = useMemo(() => getInvoiceFormSchema(showVat), [showVat])

  const defaultValues = useMemo(
    () => getInvoiceFormDefaults(data, config?.region.countryCode),
    [data, config?.region.countryCode]
  )

  const form = useForm({
    defaultValues,
    resolver: zodResolver(schema) as any,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues])

  useEffect(() => {
    submitRef.current = () => {
      if (!kids && !hasAnyInvoiceFormValues(form.getValues())) {
        updateData({ invoice: undefined })
        onSuccess()
        return
      }

      void form.handleSubmit(
        (values) => {
          updateData({
            invoice: {
              ...values,
              birth_date: getSaveDate(values.birth_date),
            },
          })
          onSuccess()
        },
        () => setIsPending?.(false)
      )()
    }
  })

  return (
    <FormProvider {...form}>
      <View className="gap-8">
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="firstName"
              label={t('user:firstName')}
              labelVariant="top"
              required
              next="lastName"
              autoCapitalize="words"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="lastName"
              label={t('user:lastName')}
              labelVariant="top"
              required
              autoCapitalize="words"
            />
          </View>
        </View>
        <FormInputDate
          control={form.control}
          name="birth_date"
          label={t('user:birthDate')}
          labelVariant="top"
          required
          min={BirthdateValues.min}
          max={BirthdateValues.max}
          defaultDate={BirthdateValues.default}
        />
        {showVat && (
          <FormInput
            control={form.control}
            name="vatId"
            label={t('user:vat')}
            labelVariant="top"
          />
        )}
        <FormInput
          control={form.control}
          name="email"
          label={t('user:email')}
          labelVariant="top"
          required
        />
        <FormInput
          control={form.control}
          name="mobile"
          label={t('user:mobile')}
          labelVariant="top"
          required
          inputMode="numeric"
        />
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="street"
              label={t('user:street')}
              labelVariant="top"
              required
              next="street2"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="street2"
              label={t('user:street2')}
              labelVariant="top"
              required
            />
          </View>
        </View>
        <View className="flex-row gap-8">
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="zip"
              label={t('user:zip')}
              labelVariant="top"
              required
              next="city"
            />
          </View>
          <View className="flex-1">
            <FormInput
              control={form.control}
              name="city"
              label={t('user:city')}
              labelVariant="top"
              required
            />
          </View>
          <View className="flex-1">
            <FormSelect
              control={form.control}
              name="country_code"
              label={t('user:country')}
              labelVariant="top"
              required
              options={countries}
              placeholder={t('user:address.country')}
            />
          </View>
        </View>
      </View>
    </FormProvider>
  )
}
